import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as magazineActions from "../../redux/megazine/megazine.actions";
import moment from "moment";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
// import { X } from "lucide-react";
import Sidebar from "./Sidebar";
import MagazineList from "./MagazineList";
import { toast } from "react-toastify";
import MagazineCreateModal from "../../components/magazine/modals/MagazineCreateModal";
import MagazineDetailsModal from "../../components/magazine/modals/MagazineDetailsModal";
import DonationDetailsModal from "../../components/magazine/modals/DonationDetailsModal";
import ContactDetailsModal from "../../components/magazine/modals/ContactDetailsModal";
import HeroSectionModal from "../../components/magazine/modals/HeroSectionModal";
import MagazineSurveyModal from "../../components/magazine/modals/MagazineSurveyModal";
import AttachMagazineSurveyModal from "../../components/magazine/modals/AttachMagazineSurveyModal";

// Reusable selection input component
const SelectionInput = ({
  label,
  placeholder,
  value,
  onSearch,
  onSelect,
  onClear,
  results,
  isActive,
  onFocus,
  magazineSurveyList,
}) => {
  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <div className="relative">
        <div className="flex items-center border rounded">
          <input
            type="text"
            className="form-control border-0"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onSearch(e.target.value)}
            onFocus={onFocus}
          />
          {value && (
            <button
              className="btn btn-link text-secondary p-2"
              onClick={onClear}
              type="button"
              aria-label="Clear selection"
            >
              {/* <X size={18} /> */}
            </button>
          )}
        </div>

        {isActive && results?.length > 0 && (
          <div className="absolute w-full mt-1 bg-white border rounded shadow-lg z-10 max-h-60 overflow-y-auto">
            {!magazineSurveyList
              ? results.map((item) => (
                <div
                  key={item._id}
                  className="p-3 hover:bg-gray-100"
                  style={{ cursor: "pointer" }}
                  onClick={() => onSelect(item)}
                >
                  <h6 className="mb-0 text-primary">{item.title}</h6>
                </div>
              ))
              : results
                .filter(
                  (item) =>
                    !magazineSurveyList.some(
                      (survey) => survey?.surveyId?._id === item?._id
                    )
                )
                .map((filteredItem) => (
                  <div
                    key={filteredItem?._id}
                    className="p-3 hover:bg-gray-100"
                    style={{ cursor: "pointer" }}
                    onClick={() => onSelect(filteredItem)}
                  >
                    <h6 className="mb-0 text-primary">
                      {filteredItem?.title ? filteredItem?.title : "No title"}
                    </h6>
                  </div>
                ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Import modal components

const Magazine = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("magazine");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [heroType, setHeroType] = useState("");
  const [surveySearch, setSurveySearch] = useState("");
  const [articleSearch, setArticleSearch] = useState("");
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [heroIndex, setHeroIndex] = useState(1);
  const [activeSurveyDropdown, setActiveSurveyDropdown] = useState(false);
  const [activeArticleDropdown, setActiveArticleDropdown] = useState(false);
  const [newMagazine, setNewMagazine] = useState({
    pdfName: "",
    file: null,
  });

  const {
    list = [],
    loading,
    error,
    donationList = [],
    contactUsList = [],
    selectedMagazine,
    isMagazineStatusChange,
    heroSectionList = [],
    surveyList = { surveys: [], articles: [] },
    magazineSurveyList = [],
  } = useSelector((state) => state.magazineInfo || {});

  const toggleModal = () => {
    setSelectedSurvey(null);
    setSelectedArticles([]);
    setNewMagazine({ pdfName: "", file: null });
    setSurveySearch("");
    setArticleSearch("");
    setActiveSurveyDropdown(false);
    setSelectedItem(null);
    setHeroType("");
    setShowModal(!showModal);

    if (!showModal) {
      // Reset states when opening modal
      setNewMagazine({ pdfName: "", file: null });
      setHeroType("");
      clearSelections();
    }
  };

  useEffect(() => {
    dispatch(magazineActions.getMagazineList());
    dispatch(magazineActions.getDonationList());
    dispatch(magazineActions.getContactUsList());
    dispatch(magazineActions.getHeroSectionList());
    dispatch(magazineActions.getMagazineSurveyList());
  }, [dispatch, isMagazineStatusChange]);

  useEffect(() => {
    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest(".relative")) {
        setActiveSurveyDropdown(false);
        setActiveArticleDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const clearSelections = () => {
    setSelectedSurvey(null);
    setSelectedArticles([]);
    setSurveySearch("");
    setArticleSearch("");
    setActiveSurveyDropdown(false);
    setActiveArticleDropdown(false);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (tab === "magazine") {
      dispatch(magazineActions.getMagazineList());
    }
  };

  const handleItemClick = (item, type) => {
    setSelectedItem(item);
    setModalType(type);
    setShowModal(true);
  };

  // console.log("selectedItem", selectedItem);

  const handleDeleteMagazine = async (id) => {
    if (window.confirm("Are you sure you want to delete this magazine?")) {
      await dispatch(magazineActions.deleteMagazine(id));
      dispatch(magazineActions.getMagazineList());
    }
  };

  const handleCreateMagazine = async (e) => {
    e.preventDefault();
    if (!newMagazine.pdfName || !newMagazine.file) {
      alert("Please fill in all fields");
      return;
    }
    await dispatch(magazineActions.createMagazine(newMagazine));
    dispatch(magazineActions.getMagazineList());
    toggleModal();
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file") {
      setNewMagazine({ ...newMagazine, file: files[0] });
    } else {
      setNewMagazine({ ...newMagazine, [name]: value });
    }
  };

  const handleSurveySearch = (value) => {
    setSurveySearch(value);
    if (value) {
      dispatch(magazineActions.getSurveyList(value));
      setActiveSurveyDropdown(true);
    } else {
      setActiveSurveyDropdown(false);
    }
  };

  const handleArticleSearch = (value) => {
    setArticleSearch(value);
    if (value) {
      dispatch(magazineActions.getSurveyList(value));
      setActiveArticleDropdown(true);
    } else {
      setActiveArticleDropdown(false);
    }
  };

  const handleSurveySelect = (survey) => {
    setSelectedSurvey(survey);
    setSurveySearch(survey.title);
    setActiveSurveyDropdown(false);
  };

  const handleArticleSelect = (article) => {
    if (!selectedArticles.some(a => a.id === article.id)) {
      setSelectedArticles([...selectedArticles, article]);
    }
    setArticleSearch(article.title);
    setActiveArticleDropdown(false);
  };

  const handleRemoveArticle = (articleId) => {
    setSelectedArticles(selectedArticles.filter(article =>
      article.id !== articleId
    ));
  };

  const handleHeroSectionSubmit = async () => {
    const data = {
      type: heroType,
      surveyId: selectedSurvey?._id,
      articleSlug: selectedArticles[0]?.slug,
      index: selectedItem?.index,
    };

    const dataForCreateMagazineSurvey = {
      surveyId: selectedSurvey?._id,
      articleSlug: selectedArticles[0]?.slug,
    };

    try {
      const hasSurveyId = magazineSurveyList.some(
        (survey) => survey?.surveyId?._id === selectedSurvey?._id
      );
      if (hasSurveyId || selectedArticles[0]?.slug) {
        await dispatch(
          magazineActions.updateHeroSection(selectedItem._id, data)
        );
        await dispatch(magazineActions.getHeroSectionList());
      } else {
        await dispatch(
          magazineActions.updateHeroSection(selectedItem._id, data)
        );
        await dispatch(
          magazineActions.createMagazineSurvey(dataForCreateMagazineSurvey)
        );
        await dispatch(magazineActions.getMagazineSurveyList());
        await dispatch(magazineActions.getHeroSectionList());
      }
      setHeroType("");
      setSelectedSurvey(null);
      setSurveySearch("");
      setActiveSurveyDropdown(false);
      toggleModal();
    } catch (error) {
      console.error("Error updating hero section:", error);
    }
  };

  const handleCreateMagazineSurvey = async () => {
    const data = {
      surveyId: selectedSurvey?._id,
      articleSlug: selectedArticles[0]?.slug,
    };

    if (!data) {
      toast.error("Please fill details");
      return;
    }

    try {
      await dispatch(magazineActions.createMagazineSurvey(data));
      await dispatch(magazineActions.getMagazineSurveyList());
      setSelectedSurvey(null);
      setSurveySearch("");
      setActiveSurveyDropdown(false);
      toggleModal();
    } catch (error) {
      console.error("Error creating survey:", error);
    }
  };

  const handleAttachMagazineSurvey = async () => {
    // console.log("selectedArticles", selectedArticles);
    const data = {
      surveyId: selectedSurvey?._id,
      articleSlug: selectedArticles.map(article => article.slug),
      articleIds: selectedArticles.map(article => article.id)
    };

    try {
      await dispatch(
        magazineActions.updateMagazineSurvey(selectedItem._id, data)
      );
      await dispatch(magazineActions.getMagazineSurveyList());
      setArticleSearch("");
      setSelectedArticles([]);
      setActiveArticleDropdown(false);
      toggleModal();
    } catch (error) {
      console.error("Error updating survey:", error);
    }
  };

  const handleDeleteMagazineSurvey = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this magazine survey?")
    ) {
      await dispatch(magazineActions.deleteMagazineSurvey(id));
      dispatch(magazineActions.getMagazineSurveyList());
    }
    dispatch(magazineActions.getMagazineSurveyList());
  };

  const renderModalContent = () => {
    const itemToDisplay = selectedMagazine || selectedItem;

    switch (modalType) {
      case "magazine-create":
        return (
          <MagazineCreateModal
            isOpen={showModal}
            toggleModal={toggleModal}
            newMagazine={newMagazine}
            handleInputChange={handleInputChange}
            handleCreateMagazine={handleCreateMagazine}
          />
        );

      case "magazine":
        return <MagazineDetailsModal item={itemToDisplay} />;

      case "donation":
        return <DonationDetailsModal item={itemToDisplay} />;

      case "contact":
        return <ContactDetailsModal item={itemToDisplay} />;

      case "hero-section-edit":
        return (
          <HeroSectionModal
            heroType={heroType}
            setHeroType={setHeroType}
            surveySearch={surveySearch}
            articleSearch={articleSearch}
            selectedSurvey={selectedSurvey}
            selectedArticles={selectedArticles}
            activeSurveyDropdown={activeSurveyDropdown}
            activeArticleDropdown={activeArticleDropdown}
            surveyList={surveyList}
            handleSurveySearch={handleSurveySearch}
            handleArticleSearch={handleArticleSearch}
            handleSurveySelect={handleSurveySelect}
            handleArticleSelect={handleArticleSelect}
            selectedItem={selectedItem}
            heroIndex={heroIndex}
            setHeroIndex={setHeroIndex}
            handleHeroSectionSubmit={handleHeroSectionSubmit}
            clearSelections={clearSelections}
          />
        );

      case "create-magazine-survey":
        return (
          <MagazineSurveyModal
            surveySearch={surveySearch}
            selectedSurvey={selectedSurvey}
            activeSurveyDropdown={activeSurveyDropdown}
            surveyList={surveyList}
            magazineSurveyList={magazineSurveyList}
            handleSurveySearch={handleSurveySearch}
            handleSurveySelect={handleSurveySelect}
            handleCreateMagazineSurvey={handleCreateMagazineSurvey}
            clearSelections={clearSelections}
          />
        );

      case "attach-magazine-survey":
        return (
          <AttachMagazineSurveyModal
            articleSearch={articleSearch}
            selectedArticles={selectedArticles}
            activeArticleDropdown={activeArticleDropdown}
            surveyList={surveyList}
            handleArticleSearch={handleArticleSearch}
            handleArticleSelect={handleArticleSelect}
            handleAttachMagazineSurvey={handleAttachMagazineSurvey}
            clearSelections={clearSelections}
            handleRemoveArticle={handleRemoveArticle}
          />
        );

      default:
        return null;
    }
  };

  if (error) {
    return <div className="text-center p-5 text-danger">{error}</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <Sidebar activeTab={activeTab} handleTabClick={handleTabClick} />
        <MagazineList
          activeTab={activeTab}
          handleDeleteMagazineSurvey={handleDeleteMagazineSurvey}
          handleItemClick={handleItemClick}
          handleDeleteMagazine={handleDeleteMagazine}
          list={list}
          donationList={donationList}
          contactUsList={contactUsList}
          loading={loading}
          error={error}
          heroSectionList={heroSectionList}
          magazineSurveyList={magazineSurveyList}
        />
      </div>

      <Modal isOpen={showModal} toggle={toggleModal} size="lg">
        {renderModalContent()}
        {modalType !== "magazine-create" && (
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Close
            </Button>
          </ModalFooter>
        )}
      </Modal>
    </div>
  );
};

export default Magazine;
